/**
 * Analytics Event Tracking (T168)
 *
 * Custom business event tracking for the Yi Dashboard.
 * Integrates with Sentry for breadcrumbs and can optionally send to
 * a custom analytics endpoint for business metrics.
 *
 * Tracked Events:
 * - Episode lifecycle: created, published, unpublished
 * - Asset management: uploaded, deleted
 * - Landing pages: published, unpublished
 * - Search: queries performed
 * - Navigation: page views
 */

import { addBreadcrumb, isSentryEnabled } from './sentry.client';

/**
 * Analytics event types for type safety.
 */
export type AnalyticsEventName =
  // Episode events
  | 'episode_created'
  | 'episode_published'
  | 'episode_unpublished'
  | 'episode_deleted'
  | 'episode_updated'
  // Asset events
  | 'asset_uploaded'
  | 'asset_deleted'
  | 'asset_updated'
  // Landing page events
  | 'landing_page_created'
  | 'landing_page_published'
  | 'landing_page_unpublished'
  | 'landing_page_deleted'
  // Search events
  | 'search_performed'
  | 'search_filter_applied'
  // Navigation events
  | 'page_viewed'
  | 'feature_accessed'
  // Question bank events
  | 'question_created'
  | 'question_deleted'
  | 'question_bank_created'
  // General events
  | 'error_occurred'
  | 'action_completed';

/**
 * Event property types for common events.
 */
export interface AnalyticsEventProperties {
  // Common properties
  organizationId?: string;
  userId?: string;
  timestamp?: number;
  page?: string;

  // Episode properties
  episodeId?: string;
  episodeStatus?: string;
  showId?: string;
  seasonNumber?: number;

  // Asset properties
  assetId?: string;
  assetType?: 'image' | 'video' | 'audio';
  assetSize?: number;
  mimeType?: string;

  // Landing page properties
  landingPageId?: string;
  landingPageSlug?: string;

  // Search properties
  searchQuery?: string;
  searchResultCount?: number;
  searchFilters?: Record<string, unknown>;
  searchDuration?: number;

  // Question properties
  questionId?: string;
  questionBankId?: string;

  // Error properties
  errorType?: string;
  errorMessage?: string;
  errorContext?: string;

  // Generic properties
  [key: string]: unknown;
}

/**
 * Analytics configuration.
 */
interface AnalyticsConfig {
  /** Custom analytics endpoint URL */
  endpoint?: string;
  /** Whether analytics is enabled */
  enabled: boolean;
  /** Debug mode */
  debug: boolean;
  /** User context */
  user?: {
    id: string;
    organizationId?: string;
  };
}

/**
 * Global analytics configuration.
 */
let analyticsConfig: AnalyticsConfig = {
  enabled: true,
  debug: process.env.NODE_ENV === 'development',
};

/**
 * Initialize analytics tracking.
 *
 * @param config - Analytics configuration
 *
 * @example
 * ```typescript
 * initAnalytics({
 *   endpoint: 'https://analytics.yakinteractive.com/events',
 *   enabled: true,
 *   debug: process.env.NODE_ENV === 'development',
 *   user: {
 *     id: user.id,
 *     organizationId: organization.id,
 *   },
 * });
 * ```
 */
export function initAnalytics(config: Partial<AnalyticsConfig>): void {
  analyticsConfig = {
    ...analyticsConfig,
    ...config,
  };

  if (analyticsConfig.debug) {
    console.log('[Analytics] Initialized with config:', {
      enabled: analyticsConfig.enabled,
      hasEndpoint: Boolean(analyticsConfig.endpoint),
      hasUser: Boolean(analyticsConfig.user),
    });
  }
}

/**
 * Set user context for analytics.
 *
 * @param user - User information (null to clear)
 */
export function setAnalyticsUser(
  user: { id: string; organizationId?: string } | null
): void {
  analyticsConfig.user = user ?? undefined;

  if (analyticsConfig.debug) {
    console.log('[Analytics] User context updated:', user);
  }
}

/**
 * Track an analytics event.
 *
 * @param name - Event name
 * @param properties - Optional event properties
 *
 * @example
 * ```typescript
 * // Track episode published
 * trackEvent('episode_published', {
 *   episodeId: episode.id,
 *   showId: episode.showId,
 *   episodeStatus: 'published',
 * });
 *
 * // Track asset uploaded
 * trackEvent('asset_uploaded', {
 *   assetType: 'image',
 *   assetSize: file.size,
 *   mimeType: file.type,
 * });
 *
 * // Track search performed
 * trackEvent('search_performed', {
 *   searchQuery: query,
 *   searchResultCount: results.length,
 *   searchDuration: performance.now() - startTime,
 * });
 * ```
 */
export async function trackEvent(
  name: AnalyticsEventName,
  properties?: AnalyticsEventProperties
): Promise<void> {
  if (!analyticsConfig.enabled) {
    return;
  }

  // Enrich properties with context
  const enrichedProperties: AnalyticsEventProperties = {
    ...properties,
    timestamp: Date.now(),
    page: typeof window !== 'undefined' ? window.location.pathname : undefined,
    userId: analyticsConfig.user?.id ?? properties?.userId,
    organizationId:
      analyticsConfig.user?.organizationId ?? properties?.organizationId,
  };

  // Debug logging
  if (analyticsConfig.debug) {
    console.log('[Analytics] Event tracked:', name, enrichedProperties);
  }

  // Add as Sentry breadcrumb for error context
  if (isSentryEnabled()) {
    addBreadcrumb({
      category: 'analytics',
      message: name,
      level: 'info',
      data: enrichedProperties,
    });
  }

  // Send to custom analytics endpoint if configured
  if (analyticsConfig.endpoint) {
    try {
      await sendToEndpoint(name, enrichedProperties);
    } catch (error) {
      console.error('[Analytics] Failed to send event:', error);
    }
  }
}

/**
 * Send event to custom analytics endpoint.
 */
async function sendToEndpoint(
  name: AnalyticsEventName,
  properties: AnalyticsEventProperties
): Promise<void> {
  if (!analyticsConfig.endpoint) return;

  try {
    const response = await fetch(analyticsConfig.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event: name,
        properties,
        sentAt: new Date().toISOString(),
      }),
      // Don't block on analytics
      keepalive: true,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error) {
    // Log but don't throw - analytics should not break the app
    if (analyticsConfig.debug) {
      console.error('[Analytics] Send failed:', error);
    }
  }
}

/**
 * Track a page view.
 *
 * @param path - Page path (defaults to current location)
 * @param properties - Optional additional properties
 *
 * @example
 * ```typescript
 * // In a route component effect
 * useEffect(() => {
 *   trackPageView();
 * }, [location.pathname]);
 * ```
 */
export function trackPageView(
  path?: string,
  properties?: AnalyticsEventProperties
): void {
  const pagePath = path ?? (typeof window !== 'undefined' ? window.location.pathname : '/');

  trackEvent('page_viewed', {
    ...properties,
    page: pagePath,
  });
}

/**
 * Track a feature access event.
 *
 * @param feature - Feature name
 * @param properties - Optional additional properties
 *
 * @example
 * ```typescript
 * trackFeatureAccess('episode-editor');
 * trackFeatureAccess('asset-upload-modal');
 * ```
 */
export function trackFeatureAccess(
  feature: string,
  properties?: AnalyticsEventProperties
): void {
  trackEvent('feature_accessed', {
    ...properties,
    feature,
  });
}

/**
 * Track search event with timing.
 *
 * @param query - Search query
 * @param resultCount - Number of results
 * @param durationMs - Search duration in milliseconds
 * @param filters - Applied filters
 */
export function trackSearch(
  query: string,
  resultCount: number,
  durationMs: number,
  filters?: Record<string, unknown>
): void {
  trackEvent('search_performed', {
    searchQuery: query,
    searchResultCount: resultCount,
    searchDuration: durationMs,
    searchFilters: filters,
  });
}

/**
 * Track error for analytics (separate from Sentry error tracking).
 *
 * @param type - Error type/category
 * @param message - Error message
 * @param context - Additional context
 */
export function trackError(
  type: string,
  message: string,
  context?: string
): void {
  trackEvent('error_occurred', {
    errorType: type,
    errorMessage: message,
    errorContext: context,
  });
}

/**
 * Create a timing helper for measuring operations.
 *
 * @returns Object with end() method that tracks the duration
 *
 * @example
 * ```typescript
 * const timing = startTiming();
 * await performOperation();
 * timing.end('search_performed', { searchQuery: query });
 * ```
 */
export function startTiming(): {
  end: (eventName: AnalyticsEventName, properties?: AnalyticsEventProperties) => void;
} {
  const startTime = performance.now();

  return {
    end: (eventName: AnalyticsEventName, properties?: AnalyticsEventProperties) => {
      const duration = performance.now() - startTime;
      trackEvent(eventName, {
        ...properties,
        duration,
      });
    },
  };
}

/**
 * Convenience functions for common events.
 */
export const Analytics = {
  // Episode events
  episodeCreated: (episodeId: string, showId: string) =>
    trackEvent('episode_created', { episodeId, showId }),
  episodePublished: (episodeId: string, showId: string) =>
    trackEvent('episode_published', { episodeId, showId }),
  episodeUnpublished: (episodeId: string) =>
    trackEvent('episode_unpublished', { episodeId }),
  episodeDeleted: (episodeId: string) =>
    trackEvent('episode_deleted', { episodeId }),

  // Asset events
  assetUploaded: (assetType: 'image' | 'video' | 'audio', size: number, mimeType: string) =>
    trackEvent('asset_uploaded', { assetType, assetSize: size, mimeType }),
  assetDeleted: (assetId: string) =>
    trackEvent('asset_deleted', { assetId }),

  // Landing page events
  landingPagePublished: (landingPageId: string, slug: string) =>
    trackEvent('landing_page_published', { landingPageId, landingPageSlug: slug }),
  landingPageUnpublished: (landingPageId: string) =>
    trackEvent('landing_page_unpublished', { landingPageId }),

  // Search events
  search: trackSearch,

  // Page views
  pageView: trackPageView,

  // Errors
  error: trackError,
} as const;
